import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * URL Redirect Handler for SEO Migration
 * Maps old WordPress URLs to new site structure
 */

// Exact path redirects (old path → new path)
const EXACT_REDIRECTS: Record<string, string> = {
  // Main pages
  '/about-us': '/about',
  '/about-us/': '/about',
  '/contact-us': '/contact',
  '/contact-us/': '/contact',
  '/our-team': '/about', // Redirect to about until team page is created
  '/our-team/': '/about',
  
  // Service redirects (old WordPress slugs)
  '/service': '/services',
  '/service/': '/services',
  '/service/search-engine-optimization': '/services/seo',
  '/service/search-engine-optimization/': '/services/seo',
  '/service/web-development': '/services/web-design',
  '/service/web-development/': '/services/web-design',
  '/service/graphic-design': '/services/graphic-design',
  '/service/graphic-design/': '/services/graphic-design',
  '/service/brand-identity': '/services/branding',
  '/service/brand-identity/': '/services/branding',
  '/service/advertising-paid-media': '/services/ppc',
  '/service/advertising-paid-media/': '/services/ppc',
  '/service/ui-ux-design': '/services/ui-ux-design',
  '/service/ui-ux-design/': '/services/ui-ux-design',
  '/service/app-software-development': '/services/app-development',
  '/service/app-software-development/': '/services/app-development',
  
  // Blog redirects
  '/blogs': '/blog',
  '/blogs/': '/blog',
  
  // Funnel redirects
  '/free-seo-audit': '/funnel/seo-audit',
  '/free-seo-audit/': '/funnel/seo-audit',
  
  // FAQ redirect
  '/faq': '/faq',
  '/faq/': '/faq',
  
  // Terms redirect (same path but normalize)
  '/terms': '/terms',
  '/terms/': '/terms',
  
  // Landing pages with trailing slashes redirect to non-trailing versions
  '/logo-design/': '/logo-design',
  '/custom-logo-design/': '/custom-logo-design',
  '/music-logo-design/': '/music-logo-design',
  '/ecommerce-website-design/': '/ecommerce-website-design',
  '/service-website-design/': '/service-website-design',
  
  // Other landing page redirects
  '/agency-logo-design': '/logo-design',
  '/agency-logo-design/': '/logo-design',
  '/website-design': '/services/web-design',
  '/website-design/': '/services/web-design',
  '/website-design-journey': '/services/web-design',
  '/website-design-journey/': '/services/web-design',
  '/digital-marketing-excellence': '/services',
  '/digital-marketing-excellence/': '/services',
  
  // Portfolio/Gallery redirect
  '/awl_filter_gallery/vd': '/portfolio',
  '/awl_filter_gallery/vd/': '/portfolio',
  
  // Case study redirects
  '/baby-clothing-website': '/portfolio',
  '/baby-clothing-website/': '/portfolio',
  
  // Valentine SEO trailing slash redirects
  '/valentine-seo/': '/valentine-seo',
  '/valentine-seo/pricing/': '/valentine-seo/pricing',
  '/valentine-seo/case-studies/': '/valentine-seo/case-studies',
  '/valentine-seo/compare/': '/valentine-seo/compare',
  '/valentine-seo/help/': '/valentine-seo/help',
  '/valentine-seo/help-center/': '/valentine-seo/help-center',
  '/valentine-seo/tools/': '/valentine-seo/tools',
  '/valentine-seo/valentine-ai/': '/valentine-seo/valentine-ai',
};

// Pattern-based redirects (checked if exact match not found)
const PATTERN_REDIRECTS: Array<{ pattern: RegExp; replacement: string | ((match: RegExpMatchArray) => string) }> = [
  // Individual blog posts: /blogs/slug → /blog/slug
  { pattern: /^\/blogs\/([^/]+)\/?$/, replacement: (m) => `/blog/${m[1]}` },
  
  // Blog pagination: /blogs/page/X → /blog
  { pattern: /^\/blogs\/page\/\d+\/?$/, replacement: '/blog' },
  
  // Category pages: /category/X → /blog/category/X
  { pattern: /^\/category\/([^/]+)\/?$/, replacement: (m) => `/blog/category/${m[1]}` },
  
  // Category pagination: /category/X/page/Y → /blog/category/X
  { pattern: /^\/category\/([^/]+)\/page\/\d+\/?$/, replacement: (m) => `/blog/category/${m[1]}` },
  
  // Category feed: /category/X/feed → /blog/category/X
  { pattern: /^\/category\/([^/]+)\/feed\/?$/, replacement: (m) => `/blog/category/${m[1]}` },
  
  // Any /service/X pattern not matched exactly → /services
  { pattern: /^\/service\/[^/]+\/?$/, replacement: '/services' },
  
  // Valentine SEO trailing slash pattern for all sub-pages
  { pattern: /^\/valentine-seo\/([^/]+)\/$/, replacement: (m) => `/valentine-seo/${m[1]}` },
];

// Paths that should return 404 (WordPress artifacts)
const EXCLUDED_PATTERNS: RegExp[] = [
  /\/feed\/?$/,           // RSS feeds
  /\/wp-includes\//,      // WordPress assets
  /\/wp-content\//,       // WordPress content
  /\/wp-admin\//,         // WordPress admin
  /\.php$/,               // PHP files
  /\.xml$/,               // XML files (except sitemap which should be static)
];

export function RedirectHandler({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const currentPath = location.pathname;
    
    // Check if this is an excluded pattern (should 404)
    for (const pattern of EXCLUDED_PATTERNS) {
      if (pattern.test(currentPath)) {
        // Let it fall through to 404
        return;
      }
    }
    
    // Check for exact redirects first
    const exactRedirect = EXACT_REDIRECTS[currentPath];
    if (exactRedirect && exactRedirect !== currentPath) {
      console.log(`Redirecting: ${currentPath} → ${exactRedirect}`);
      navigate(exactRedirect, { replace: true });
      return;
    }
    
    // Check pattern-based redirects
    for (const { pattern, replacement } of PATTERN_REDIRECTS) {
      const match = currentPath.match(pattern);
      if (match) {
        const newPath = typeof replacement === 'function' ? replacement(match) : replacement;
        if (newPath !== currentPath) {
          console.log(`Pattern redirect: ${currentPath} → ${newPath}`);
          navigate(newPath, { replace: true });
          return;
        }
      }
    }
  }, [location.pathname, navigate]);

  return <>{children}</>;
}

export default RedirectHandler;
